import { isLoginUrl } from '@/services/repository/session'
import { Services } from '@/services/services'
import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { canGoBackInApp } from '@/common/reactnav'

// Define the shape of the context
interface LoginContextType {
  redirectToLogin: (params: {redirectTo?: string, replace?: boolean}) => void
  handleLogin: (userData: any) => void
  handleLogout: () => void
  goBackAfterLogin: () => void
}

// Provide a default value to avoid the error
const LoginContext = createContext<LoginContextType | undefined>(undefined)

// Define props type for LoginProvider
interface LoginProviderProps {
  services: Services
  children: ReactNode 
}

export const LoginProvider: React.FC<LoginProviderProps> = ({ children, services }) => {
  const [redirectionPath, setRedirectionPath] = useState('/dashboard')
  // true when /login was pushed on top of redirectionPath, so returning is a history step back
  const [returnByBack, setReturnByBack] = useState(false)
  // const [isAuthenticated, setIsAuthenticated] = useState(false)
  // const [session, setSession] = useState<CurrentSession>()

  // useEffect(() => {
  //   const id = services.session.listen((current) => {
  //     setSession(current)
  //   })
  //   return () => {
  //     services.session.remove(id)
  //   }
  // }, [])

  const navigate = useNavigate()
  const location = useLocation()

  // replace: true is for automatic redirects, so the protected page that bounced
  // to /login does not stay in history.
  const redirectToLogin = ({redirectTo = location.pathname + location.search + location.hash, replace = false}: {redirectTo?: string, replace?: boolean}) => {
    setRedirectionPath(redirectTo)
    setReturnByBack(!replace)
    navigate('/login', { replace })
  }

  // Leaves /login without keeping it in history: step back when /login was opened
  // on top of the target page, otherwise replace the /login entry with the target.
  const returnFromLogin = (path: string) => {
    // Both / and /login render LoginPage. React can reuse the mounted form when
    // moving between them, so its mount-only redirect will not run after login.
    if (isLoginUrl(path.split(/[?#]/)[0])) {
      navigate('/dashboard', { replace: true })
      return
    }
    if (returnByBack && canGoBackInApp()) {
      navigate(-1)
      return
    }
    navigate(path, { replace: true })
  }

  const handleLogin = (userData: any) => {
    // setIsAuthenticated(true)
    returnFromLogin(redirectionPath)
  }

  const handleLogout = () => {
    setRedirectionPath('/dashboard')
    setReturnByBack(false)
    navigate('/logout')
  }

  const goBackAfterLogin = () => {
    const path = redirectionPath
    setRedirectionPath('/dashboard')
    setReturnByBack(false)
    returnFromLogin(path)
  }

  return (
    <LoginContext.Provider
      value={{ redirectToLogin, handleLogin, handleLogout, goBackAfterLogin }}
    >
      {children}
    </LoginContext.Provider>
  )
}

export const useLogin = (): LoginContextType => {
  const context = useContext(LoginContext)
  if (!context) {
    throw new Error('useLogin must be used within a LoginProvider')
  }
  return context
}
